import { useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Package, Scale, ChefHat, Award } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface Product {
  id: number
  name: string
  category: 'panettoni' | 'colombe' | 'pasticceria'
  subcategory?: string
  image: string
  description?: string
  ingredients?: string[]
  weight?: string
  format?: string
  isNew?: boolean
  isClassic?: boolean
  badges?: string[]
}

interface ProductModalProps {
  product: Product | null
  isOpen: boolean
  onClose: () => void
}

export function ProductModal({ product, isOpen, onClose }: ProductModalProps) {
  const { t } = useTranslation('common')

  // Handle escape key
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose()
  }, [onClose])

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [isOpen, handleEscape])

  if (!isOpen || !product) return null

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={cn(
          'relative w-full max-w-4xl max-h-[90vh] overflow-hidden',
          'bg-white rounded-2xl shadow-2xl',
          'transform transition-all duration-300 scale-100',
          'flex flex-col md:flex-row'
        )}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className={cn(
            'absolute top-4 right-4 z-10',
            'w-10 h-10 rounded-full bg-white/90 backdrop-blur',
            'flex items-center justify-center',
            'shadow-lg hover:shadow-xl',
            'transition-all duration-200 hover:scale-110',
            'text-neutral-600 hover:text-neutral-900'
          )}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Image Section */}
        <div className="relative w-full md:w-1/2 bg-gradient-to-br from-neutral-100 to-neutral-200 min-h-[300px] md:min-h-[500px]">
          {/* Badges */}
          <div className="absolute top-4 left-4 flex flex-wrap gap-2">
            {product.isNew && (
              <span className="px-3 py-1 bg-brand text-neutral-900 text-xs font-bold uppercase tracking-wider rounded-full">
                {t('productLabels.new')}
              </span>
            )}
            {product.isClassic && (
              <span className="px-3 py-1 bg-amber-600 text-white text-xs font-bold uppercase tracking-wider rounded-full">
                {t('productLabels.classic')}
              </span>
            )}
            {product.badges?.map((badge, idx) => (
              <span
                key={idx}
                className="px-3 py-1 bg-neutral-800 text-white text-xs font-bold uppercase tracking-wider rounded-full"
              >
                {badge}
              </span>
            ))}
          </div>

          {/* Product Image */}
          <div className="absolute inset-0 flex items-center justify-center p-8">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-contain drop-shadow-2xl hover:scale-105 transition-transform duration-500"
            />
          </div>
        </div>

        {/* Content Section */}
        <div className="w-full md:w-1/2 p-6 md:p-8 lg:p-10 overflow-y-auto max-h-[50vh] md:max-h-[90vh]">
          {/* Category */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm font-medium text-brand uppercase tracking-wider">
              {product.category}
            </span>
            {product.subcategory && (
              <>
                <span className="text-neutral-300">|</span>
                <span className="text-sm text-neutral-500">
                  {product.subcategory}
                </span>
              </>
            )}
          </div>

          {/* Title */}
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-neutral-900 mb-6">
            {product.name}
          </h2>

          {/* Description */}
          {product.description && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <Award className="w-5 h-5 text-brand" />
                <h3 className="font-heading font-semibold text-neutral-900">
                  {t('productLabels.description')}
                </h3>
              </div>
              <p className="text-neutral-600 leading-relaxed">
                {product.description}
              </p>
            </div>
          )}

          {/* Details Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            {product.weight && (
              <div className="flex items-start gap-3 p-4 bg-neutral-50 rounded-xl">
                <div className="w-10 h-10 bg-brand/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Scale className="w-5 h-5 text-brand" />
                </div>
                <div>
                  <p className="text-sm text-neutral-500 mb-1">{t('productLabels.weight')}</p>
                  <p className="font-semibold text-neutral-900">{product.weight}</p>
                </div>
              </div>
            )}
            {product.format && (
              <div className="flex items-start gap-3 p-4 bg-neutral-50 rounded-xl">
                <div className="w-10 h-10 bg-brand/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Package className="w-5 h-5 text-brand" />
                </div>
                <div>
                  <p className="text-sm text-neutral-500 mb-1">{t('productLabels.format')}</p>
                  <p className="font-semibold text-neutral-900">{product.format}</p>
                </div>
              </div>
            )}
          </div>

          {/* Ingredients */}
          {product.ingredients && product.ingredients.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <ChefHat className="w-5 h-5 text-brand" />
                <h3 className="font-heading font-semibold text-neutral-900">
                  {t('productLabels.mainIngredients')}
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.ingredients.map((ingredient, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1.5 bg-amber-50 text-amber-800 text-sm rounded-lg border border-amber-100"
                  >
                    {ingredient}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Brand Signature */}
          <div className="pt-6 border-t border-neutral-200">
            <p className="text-sm text-neutral-500 italic">
              "{t(`productLabels.quotes.${product.category}`, { defaultValue: t('productLabels.quotes.default') })}"
            </p>
            <p className="text-xs text-neutral-400 mt-2">{t('productLabels.brandSignature')}</p>
          </div>
        </div>
      </div>
    </div>
  )
}
